import type { Formatter } from "@kovernow/flows/format";
import { dailyCost, discountBadge, planTermKey, type Quote } from "@kovernow/flows/quote";
import { Badge } from "@kovernow/react-ui/components/badge";
import { RadioGroupItem } from "@kovernow/react-ui/components/radio-group";
import { cn } from "@kovernow/react-ui/lib/utils";
import { useT } from "../../core/use-t";

/**
 * One server-priced plan in the Choose plan RadioGroup. Its accessible name
 * is the term plus the price; the price already includes any auto-applied
 * discount (no strikethrough). Every amount is the server's.
 */
export function PlanCard({
  quote,
  selected,
  money,
  underwriterName,
}: {
  quote: Quote;
  selected: boolean;
  /** Formats in the quote's currency. */
  money: Formatter;
  underwriterName?: string;
}) {
  const t = useT();
  const id = `plan-${quote.plan}`;
  const term = t(planTermKey(quote.plan));
  const price = money.currency(quote.pricing.payment.invoiceTotal);
  const daily = dailyCost(quote);
  const badge = discountBadge(quote);
  const itemValue = quote.pricing.itemValue;
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border-2 bg-card p-4",
        selected ? "border-primary" : "border-border",
      )}
      data-testid="plan-card"
      data-plan={quote.plan}
      data-selected={selected ? "true" : "false"}
    >
      <RadioGroupItem value={quote.plan} id={id} aria-label={t("choosePlan.cardLabel", { term, price })} className="mt-1" />
      <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1">
        {badge ? (
          <Badge variant="secondary" data-testid="plan-badge">
            {badge.kind === "percent"
              ? t("choosePlan.badge.percent", { percent: money.percent(badge.percent) })
              : t("choosePlan.badge.amount", { amount: money.currency(badge.amount) })}
          </Badge>
        ) : null}
        <span className="font-semibold">{term}</span>
        <span className="text-xl font-semibold tabular-nums" data-testid="plan-price">
          {price}
        </span>
        {daily !== undefined ? (
          <span className="text-sm text-muted-foreground tabular-nums" data-testid="plan-daily">
            {t("choosePlan.perDay", { amount: money.currency(daily) })}
          </span>
        ) : null}
        {typeof itemValue === "number" && itemValue > 0 ? (
          <span className="text-sm text-muted-foreground">
            {underwriterName
              ? t("choosePlan.coversUpToBy", { value: money.currency(itemValue), underwriter: underwriterName })
              : t("choosePlan.coversUpTo", { value: money.currency(itemValue) })}
          </span>
        ) : null}
      </label>
    </div>
  );
}
